import React, { useContext } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { TestDto } from 'api/Clients/Types/TestDto'
import {
  clientTestTypes,
  getTestSubcategoryByName,
  TestSubcategory,
} from 'api/Clients/Types/TestQuestionDto'
import { TickmillProductType } from 'api/Clients/Types/TickmillProductDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { FlexBox } from 'components/Box'
import MoreButton from 'components/MoreButton/MoreButton'
import { ChevronDown } from 'lucide-react'
import { useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { handleOpenWindow } from 'utils/openTestWindow'
import { hasPermission } from 'utils/permissionsValidator'
import { TenantFeature } from 'utils/Tenant'

interface Props {
  data: ClientDetailedDto
}

const ClientTestPage: React.FC<Props> = ({ data }) => {
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [auth] = useContext(AuthSessionContext)
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)
  const { callback: clientTests } = useCallbackWithForceRefresh(
    () => clientsApiClient.getClientTests(data.id),
    [data.id]
  )
  const { data: tests } = useFetchOne(clientTests)
  const permissions = auth?.permissions || []

  const handleOpenFilledTest = ({ id, test, tickmillProductId }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientTestFilledWindow,
      pathParams: {
        type: clientTestTypes.client,
        clientId: data.id ? data.id : '',
        testId: id ?? '',
      },
      additionalParams: {
        productId: tickmillProductId.toString(),
        subcategoryId: getTestSubcategoryByName(test.subcategory).toString(),
      },
    })
  }

  const handleEditTestForm = ({ id, test, tickmillProductId }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientEditTestFormWindow,
      pathParams: {
        type: clientTestTypes.client,
        clientId: data.id ? data.id : '',
        testId: id ?? '',
      },
      additionalParams: {
        productId: tickmillProductId.toString(),
        subcategoryId: getTestSubcategoryByName(test.subcategory).toString(),
      },
    })
  }

  const handleAddNewTest = ({
    subcategoryId,
    productId,
  }: {
    subcategoryId: TestSubcategory
    productId: TickmillProductType
  }) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientNewTestFormWindow,
      pathParams: { type: clientTestTypes.client, clientId: data.id ? data.id : '' },
      additionalParams: {
        subcategoryId: subcategoryId.toString(),
        productId: productId.toString(),
      },
    })
  }

  return (
    <React.Fragment>
      <FlexBox style={{ marginBottom: 36 }} alignItems='center' justifyContent='flex-start'>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type='button'
                disabled={!hasPermission(permissions, PermissionsEnum.CanAddClientTests)}
              />
            }
          >
            Add new <ChevronDown />
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start'>
            <DropdownMenuItem
              onSelect={() =>
                handleAddNewTest({
                  subcategoryId: TestSubcategory.AppTestFull,
                  productId: TickmillProductType.CFD,
                })
              }
            >
              FX/CFD (Full)
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                handleAddNewTest({
                  subcategoryId: TestSubcategory.Reduced,
                  productId: TickmillProductType.CFD,
                })
              }
            >
              FX/CFD (Reduced)
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                handleAddNewTest({
                  subcategoryId: TestSubcategory.AppTestFull,
                  productId: TickmillProductType.ETD,
                })
              }
            >
              ETD (Full)
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                handleAddNewTest({
                  subcategoryId: TestSubcategory.Reduced,
                  productId: TickmillProductType.ETD,
                })
              }
            >
              ETD (Reduced)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </FlexBox>
      <Grid data={tests || []} resizable>
        <Column
          field='test.name'
          title='Name'
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td
              className='table-columns__link'
              onClick={() => handleOpenFilledTest(gridCellProps.dataItem)}
            >
              {gridCellProps.dataItem.test.name}
            </td>
          )}
        />
        <Column title='Version' field='test.version' className='table-columns__content' />
        <Column title='User' field='user.name' className='table-columns__content' />
        <Column
          field='tickmillProductId'
          title='Type'
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td>
              {
                tickmillProducts.find(
                  (product) => product.id === gridCellProps.dataItem.tickmillProductId
                )?.name
              }
            </td>
          )}
        />
        <Column
          field='dateTaken'
          title='Date Submitted'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.dateTaken, true)}
            </td>
          )}
        />
        <Column field='totalScore' title='Score' className='table-columns__number' />
        <Column field='outcome.name' title='Outcome' className='table-columns__content' />
        {hasPermission(permissions, PermissionsEnum.CanAddClientTests) && (
          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEditTestForm(gridCellProps.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </React.Fragment>
  )
}

export default ClientTestPage
